import Button from '@/components/Button/Button';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useFeedbackSourceStore from '@/stores/Feedback/FeedbackSourceStore';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import IntegrationImportModal from '../../components/IntegrationImportModal';
import { ProviderContainer } from '../../components/ProviderContainer';
import type { IntegrationItemProps } from '../../types';
import type { AtlasInfo } from '../types';
import { AtlasConnectModal } from './AtlasConnectModal';
import { useAtlas } from './useAtlas';

export const AtlasItem = (props: IntegrationItemProps) => {
  const { isConnector } = props;
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const fetchFeedbackSources = useFeedbackSourceStore(
    (state) => state.fetchFeedbackSources,
  );

  const { atlasWorkspace, disconnectAtlasMutation, requestImportMutation } =
    useAtlas(!isConnector);

  const connectModalState = useModalState();
  const importModalState = useModalState<{
    workspace: AtlasInfo;
    isNew: boolean;
  }>();

  const handleConnectComplete = (workspace: AtlasInfo) => {
    if (!currWorkspace) return;
    fetchFeedbackSources(currWorkspace.alias);
    connectModalState.close();
    importModalState.open({
      modalState: {
        workspace,
        isNew: true,
      },
    });
  };

  const handleDisconnect = () => {
    disconnectAtlasMutation();
  };

  const handleImportWorkspace = async (dateRange: NullableDateRangeT) => {
    requestImportMutation(dateRange);
    importModalState.close();
  };

  return (
    <>
      <ProviderContainer
        multiInfoEnabled={false}
        connected={!!atlasWorkspace}
        source={SOURCE_TYPE.ATLAS}
        description="Import conversations as feedbacks into Syncly."
        connectedDisplay="Atlas workspace"
        info={atlasWorkspace!}
        onDisconnect={handleDisconnect}
        onConnectClick={() => connectModalState.open()}
        additionalActions={(workspace: AtlasInfo) => (
          <div>
            <Button
              variant="outlined"
              onClick={() =>
                importModalState.open({
                  modalState: {
                    workspace: workspace,
                    isNew: false,
                  },
                })
              }
            >
              One-time import
            </Button>
          </div>
        )}
      />
      <Modal
        status={connectModalState.status}
        closeModal={connectModalState.close}
      >
        <AtlasConnectModal onConnectComplete={handleConnectComplete} />
      </Modal>
      <Modal
        status={importModalState.status}
        closeModal={importModalState.close}
      >
        <IntegrationImportModal
          isNew={importModalState.modalState?.isNew || false}
          title={'Import feedback'}
          subTitle={'Atlas'}
          subTitleList={atlasWorkspace?.alias}
          historyList={atlasWorkspace?.history || []}
          onSubmitImport={(start_date, end_date) =>
            handleImportWorkspace({ start_date, end_date })
          }
        />
      </Modal>
    </>
  );
};
